import React, { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import {
  isEnabledAutomaticExpectedBehaviorMetric,
  resolveMetricDescription,
  useMetricDirectory,
} from "@/metrics/registry"
import { useCreatorResolver } from "@/organization"
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tooltip,
} from "@coval/ui"
import { Calendar, ExternalLink, User } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"
import type { MetricRecordData } from "@/types/metrics/contracts"
import { resourceHeaderAttribution } from "@/utils/resourceAttribution"

interface ClickableMetricTitleProps {
  metric: MetricRecordData
  className?: string
  displayText?: string
}

export const ClickableMetricTitle = React.forwardRef<
  HTMLButtonElement,
  ClickableMetricTitleProps
>(({ metric, className = "", displayText }, ref) => {
  const router = useRouter()
  const {
    resolveCreator,
    error: creatorsError,
    retry: retryCreators,
  } = useCreatorResolver({ canonical: true })
  const authorship = resourceHeaderAttribution(metric.attribution, {
    resolveCreator,
    error: creatorsError,
    retry: retryCreators,
  })
  const queryParams = useQueryParams()
  const [isOpen, setIsOpen] = useState(false)
  const { byManager } = useMetricDirectory()

  const displayName =
    displayText || metric.metric_display_name || metric.metric_name
  const description =
    resolveMetricDescription(
      byManager[metric.metric_manager ?? ""],
      metric.description,
    ) ||
    metric.metric_metadata?.question ||
    "No description available"

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const getMetricUrl = () => {
    const slug = router.query.slug as string
    if (metric.id) {
      return queryParams.getHref(`/metrics/${metric.id}`, slug)
    }
    return queryParams.getHref("/metrics", slug)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return "N/A"
    }
    return new Date(dateString).toLocaleDateString()
  }

  const getMetricTypeDisplay = () => {
    if (metric.metric_manager) {
      if (metric.metric_manager.includes("default_categorical")) {
        return "Categorical"
      }
      if (metric.metric_manager === "manager_llm_binary") {
        return "Binary LLM Judge"
      }
      if (
        metric.metric_manager.includes("numeric") ||
        metric.metric_manager.includes("numerical")
      ) {
        return "Numerical LLM Judge"
      }
    }

    if (metric.metric_metadata?.question) {
      if (
        metric.metric_metadata.additional_information?.key === "categories" &&
        metric.metric_metadata.additional_information?.value
      ) {
        return "Categorical"
      }
      if (
        metric.metric_metadata.min_value !== undefined &&
        metric.metric_metadata.max_value !== undefined
      ) {
        return "Numerical LLM Judge"
      }
      return "Binary LLM Judge"
    }

    return metric.metric_manager || "Custom"
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          ref={ref}
          className={`text-left cursor-pointer ${className}`}
          onClick={handleTitleClick}
          type="button"
        >
          <Tooltip content={description} className="max-w-xs p-2 text-sm">
            <span>{displayName}</span>
          </Tooltip>
        </button>
      </DialogTrigger>
      <DialogContent size="md" className="max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-xl">{displayName}</DialogTitle>
          <DialogDescription>{metric.metric_name}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-coval-text mb-2">
                Description
              </h3>
              <div className="p-3 bg-surface-1 rounded-md">
                <p className="text-sm whitespace-pre-wrap wrap-break-word">
                  {description}
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-coval-text mb-2">
                Metric Details
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-coval-text-muted">Type:</span>
                  <Badge variant="info">{getMetricTypeDisplay()}</Badge>
                </div>

                {metric.metric_metadata?.units && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-coval-text-muted">
                      Units:
                    </span>
                    <span className="text-sm font-medium">
                      {metric.metric_metadata.units}
                    </span>
                  </div>
                )}

                {metric.metric_metadata?.min_value !== undefined &&
                  metric.metric_metadata?.max_value !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-coval-text-muted">
                        Range:
                      </span>
                      <span className="text-sm font-medium">
                        {metric.metric_metadata.min_value} -{" "}
                        {metric.metric_metadata.max_value}
                      </span>
                    </div>
                  )}

                {metric.category && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-coval-text-muted">
                      Category:
                    </span>
                    <Badge>{metric.category}</Badge>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-coval-text-muted">Status:</span>
                  <Badge variant={metric.active ? "success" : "default"}>
                    {metric.active ? "Active" : "Inactive"}
                  </Badge>
                </div>

                {metric.locked && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-coval-text-muted">
                      Locked:
                    </span>
                    <Badge variant="warning">Yes</Badge>
                  </div>
                )}
              </div>
            </div>

            {(authorship?.createdBy || authorship?.updatedBy) && (
              <div>
                <h3 className="text-sm font-medium text-coval-text mb-2">
                  Metadata
                </h3>
                <div className="space-y-2">
                  {metric.attribution?.created_at && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-coval-text-muted" />
                      <span className="text-sm text-coval-text-muted">
                        Created:
                      </span>
                      <span className="text-sm">
                        {formatDate(metric.attribution?.created_at)}
                      </span>
                    </div>
                  )}
                  {authorship?.createdBy && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-coval-text-muted" />
                      <span className="text-sm text-coval-text-muted">
                        Created by:
                      </span>
                      <span className="text-sm">{authorship.createdBy}</span>
                    </div>
                  )}
                  {metric.attribution?.updated_at && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-coval-text-muted" />
                      <span className="text-sm text-coval-text-muted">
                        Last updated:
                      </span>
                      <span className="text-sm">
                        {formatDate(metric.attribution?.updated_at)}
                      </span>
                    </div>
                  )}
                  {authorship?.updatedBy && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-coval-text-muted" />
                      <span className="text-sm text-coval-text-muted">
                        Updated by:
                      </span>
                      <span className="text-sm">{authorship.updatedBy}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {creatorsError &&
              (metric.attribution?.created_by ||
                metric.attribution?.updated_by) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    void retryCreators()
                  }}
                >
                  Retry author names
                </Button>
              )}
            {metric.metric_metadata?.additional_information && (
              <div>
                <h3 className="text-sm font-medium text-coval-text mb-2">
                  Additional Information
                </h3>
                <div className="p-3 bg-surface-1 rounded-md">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-coval-text-muted">
                      {metric.metric_metadata.additional_information.key ===
                      "categories"
                        ? "Categories"
                        : metric.metric_metadata.additional_information.key}
                      :
                    </span>
                    <span className="text-sm ml-2">
                      {metric.metric_metadata.additional_information.key ===
                        "categories" &&
                      typeof metric.metric_metadata.additional_information
                        .value === "string"
                        ? metric.metric_metadata.additional_information.value
                            .split(",")
                            .map((cat: string) => cat.trim())
                            .join(", ")
                        : metric.metric_metadata.additional_information.value}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          {!isEnabledAutomaticExpectedBehaviorMetric(metric) && (
            <Button asChild variant="secondary" className="mr-auto">
              <Link href={getMetricUrl()}>
                <ExternalLink className="w-4 h-4" aria-hidden="true" />
                View Metric
              </Link>
            </Button>
          )}
          <DialogClose asChild>
            <Button variant="ghost">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})

ClickableMetricTitle.displayName = "ClickableMetricTitle"
